import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PricingCalculator = () => {
  const [selectedService, setSelectedService] = useState('');
  const [projectScope, setProjectScope] = useState('');
  const [duration, setDuration] = useState('');
  const [urgency, setUrgency] = useState('');

  const serviceOptions = [
    { value: '', label: 'Sélectionnez un service' },
    { value: 'consulting', label: 'Conseil en Gestion', description: 'À partir de 500,000 CDF' },
    { value: 'legal', label: 'Services Juridiques', description: 'À partir de 750,000 CDF' },
    { value: 'realestate', label: 'Immobilier Commercial', description: 'À partir de 1,000,000 CDF' },
    { value: 'it', label: 'Solutions IT', description: 'À partir de 2,000,000 CDF' },
    { value: 'logistics', label: 'Logistique & Transport', description: 'À partir de 300,000 CDF' },
    { value: 'events', label: 'Gestion d\'Événements', description: 'À partir de 1,500,000 CDF' }
  ];

  const scopeOptions = [
    { value: '', label: 'Portée du projet' },
    { value: 'small', label: 'Petit projet', description: 'Tâches simples et ponctuelles' },
    { value: 'medium', label: 'Projet moyen', description: 'Projets multi-phases' },
    { value: 'large', label: 'Grand projet', description: 'Projets complexes et étendus' },
    { value: 'enterprise', label: 'Niveau entreprise', description: 'Solutions complètes' }
  ];

  const durationOptions = [
    { value: '', label: 'Durée estimée' },
    { value: '1week', label: '1 semaine', description: 'Livraison rapide' },
    { value: '2weeks', label: '2 semaines', description: 'Standard' },
    { value: '1month', label: '1 mois', description: 'Projet moyen' },
    { value: '3months', label: '3 mois', description: 'Projet long terme' },
    { value: '6months', label: '6+ mois', description: 'Engagement étendu' }
  ];

  const urgencyOptions = [
    { value: '', label: 'Niveau d\'urgence' },
    { value: 'standard', label: 'Standard', description: 'Délai normal' },
    { value: 'priority', label: 'Prioritaire', description: '+20% sur le prix' },
    { value: 'urgent', label: 'Urgent', description: '+40% sur le prix' },
    { value: 'emergency', label: 'Urgence', description: '+60% sur le prix' }
  ];

  const calculateEstimate = () => {
    if (!selectedService || !projectScope || !duration || !urgency) {
      return null;
    }

    const baseRates = {
      consulting: 500000,
      legal: 750000,
      realestate: 1000000,
      it: 2000000,
      logistics: 300000,
      events: 1500000
    };

    const scopeMultipliers = {
      small: 1,
      medium: 2,
      large: 4,
      enterprise: 8
    };

    const durationMultipliers = {
      '1week': 1,
      '2weeks': 1.8,
      '1month': 3.5,
      '3months': 10,
      '6months': 18
    };

    const urgencyMultipliers = {
      standard: 1,
      priority: 1.2,
      urgent: 1.4,
      emergency: 1.6
    };

    const base = baseRates?.[selectedService] || 0;
    const scopeMult = scopeMultipliers?.[projectScope] || 1;
    const durationMult = durationMultipliers?.[duration] || 1;
    const urgencyMult = urgencyMultipliers?.[urgency] || 1;

    const estimate = base * scopeMult * durationMult * urgencyMult;
    const min = estimate * 0.85;
    const max = estimate * 1.15;

    return { min, estimate, max };
  };

  const estimate = calculateEstimate();

  return (
    <div className="bg-slate-800/40 backdrop-blur-xl border border-white/10 rounded-3xl p-4 md:p-6 shadow-2xl mb-6 md:mb-8">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-600 to-blue-800 shadow-lg shadow-blue-900/30 flex items-center justify-center flex-shrink-0">
          <Icon name="Calculator" size={24} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-headline)' }}>
            Calculateur de prix
          </h2>
          <p className="text-sm text-slate-400">
            Obtenez une estimation instantanée pour votre projet
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Select
          label="Type de service"
          options={serviceOptions}
          value={selectedService}
          onChange={setSelectedService}
          required
        />

        <Select
          label="Portée du projet"
          options={scopeOptions}
          value={projectScope}
          onChange={setProjectScope}
          required
        />

        <Select
          label="Durée estimée"
          options={durationOptions}
          value={duration}
          onChange={setDuration}
          required
        />

        <Select
          label="Niveau d'urgence"
          options={urgencyOptions}
          value={urgency}
          onChange={setUrgency}
          required
        />
      </div>
      {estimate ? (
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Estimation du projet</h3>
            <Icon name="TrendingUp" size={24} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-xs text-white/70 mb-1">Minimum</p>
              <p className="text-xl font-bold">{estimate?.min?.toLocaleString()} CDF</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4 border-2 border-white/20">
              <p className="text-xs text-white/70 mb-1">Estimation</p>
              <p className="text-2xl font-bold">{estimate?.estimate?.toLocaleString()} CDF</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-xs text-white/70 mb-1">Maximum</p>
              <p className="text-xl font-bold">{estimate?.max?.toLocaleString()} CDF</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="default"
              className="flex-1 bg-white text-blue-700 hover:bg-white/90"
              iconName="Send"
              iconPosition="left"
            >
              Demander un devis détaillé
            </Button>
            <Button
              variant="outline"
              className="flex-1 bg-white/10 text-white border-white/30 hover:bg-white/20"
              iconName="Calendar"
              iconPosition="left"
            >
              Planifier une consultation
            </Button>
          </div>

          <p className="text-xs text-white/70 text-center mt-4">
            * Prix indicatifs. Devis final après analyse détaillée de vos besoins.
          </p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-white/10 rounded-xl p-8 text-center">
          <Icon name="Info" size={48} className="text-slate-500 mx-auto mb-3" />
          <p className="text-sm text-slate-400">
            Remplissez tous les champs pour obtenir une estimation de prix
          </p>
        </div>
      )}
    </div>
  );
};

export default PricingCalculator;
